import type { RelatorioTemposUsuario } from "../../../services/controleProducaoService";
import { rotuloUnd } from "../RelatorioConsumo/groupByUnd";

export const CRITERIO_PONTUACAO =
  "Cada dia ganha uma nota de 0 a 10 (o melhor do dia vale 10). No período, a nota é a média desses dias. Ex.: 10, 8, 7 e 5 → 7,5.";

export type PontuacaoFuncionario = {
  userId: number;
  userNome: string;
  posicao: number;
  ocorrencias: number;
  tempoMedioMs: number;
  tempoMedioFormatado: string;
  tempoFormatado: string;
  producao: number;
  producaoFormatada: string;
  undNome: string;
  pontuacaoQtd: number;
  pontuacaoTempo: number;
  pontuacao: number;
  diasComNota: number;
  notasDias: number[];
};

type DiaPontuavel = {
  userId: number;
  producaoQuantidade: number;
  tempoMedioMs: number;
  itensEncerrados: number;
};

export function formatNota(n: number): string {
  return n.toLocaleString("pt-BR", { maximumFractionDigits: 1 });
}

function roundScore(n: number): number {
  return Math.round(n * 10) / 10;
}

function media(valores: number[]): number {
  if (valores.length === 0) return 0;
  return roundScore(valores.reduce((acc, n) => acc + n, 0) / valores.length);
}

function qtyNaUnd(
  producaoPorUnd: Array<{ undNome: string; total: number }> | undefined,
  undNome: string
): number {
  const alvo = rotuloUnd(undNome).toLowerCase();
  return (
    (producaoPorUnd ?? []).find(
      (p) => rotuloUnd(p.undNome).toLowerCase() === alvo
    )?.total ?? 0
  );
}

export function escolherUndPontuacao(
  usuarios: RelatorioTemposUsuario[],
  undFiltro: string
): string {
  if (undFiltro) return rotuloUnd(undFiltro);
  const totais = new Map<string, number>();
  for (const usuario of usuarios) {
    for (const p of usuario.producaoPorUnd ?? []) {
      if (p.total <= 0) continue;
      const key = rotuloUnd(p.undNome);
      totais.set(key, (totais.get(key) ?? 0) + p.total);
    }
  }
  if (totais.size === 0) return "kg";
  let kg: string | undefined;
  let melhorUnd = "";
  let melhorTotal = -1;
  totais.forEach((total, und) => {
    if (und.toLowerCase() === "kg") kg = und;
    if (total > melhorTotal) {
      melhorTotal = total;
      melhorUnd = und;
    }
  });
  if (kg) return kg;
  return melhorUnd;
}

/** Mesma regra da TV: nota 0 a 10, metade produção, metade rapidez. */
export function pontuarDia(base: DiaPontuavel[]): Array<
  DiaPontuavel & {
    pontuacaoQtd: number;
    pontuacaoTempo: number;
    pontuacao: number;
  }
> {
  const usaQtd = base.some((u) => u.producaoQuantidade > 0);
  const qtyDe = (u: DiaPontuavel) =>
    usaQtd ? u.producaoQuantidade : u.itensEncerrados;
  const maxQty = Math.max(0, ...base.map(qtyDe));
  const comTempo = base.filter((u) => u.tempoMedioMs > 0);
  const minTempo = Math.min(
    ...comTempo.map((u) => u.tempoMedioMs),
    Number.POSITIVE_INFINITY
  );

  return base.map((u) => {
    const qty = qtyDe(u);
    const pontuacaoQtd =
      qty > 0 && maxQty > 0 ? roundScore((qty / maxQty) * 10) : 0;
    const pontuacaoTempo =
      u.tempoMedioMs > 0 && Number.isFinite(minTempo)
        ? roundScore((minTempo / u.tempoMedioMs) * 10)
        : 0;
    const pontuacao =
      pontuacaoQtd > 0 || pontuacaoTempo > 0
        ? roundScore((pontuacaoQtd + pontuacaoTempo) / 2)
        : 0;
    return { ...u, pontuacaoQtd, pontuacaoTempo, pontuacao };
  });
}

/**
 * Nota de cada dia (como na TV), depois a média dos dias filtrados.
 * `equipe` pontua o dia inteiro; `visiveis` só filtra quem aparece no ranking.
 */
export function montarPontuacaoFuncionarios(
  equipe: RelatorioTemposUsuario[],
  undFiltro: string,
  visiveis: RelatorioTemposUsuario[] = equipe
): { undNome: string; ranking: PontuacaoFuncionario[] } {
  const undNome = escolherUndPontuacao(equipe, undFiltro);
  const datas = Array.from(
    new Set(equipe.flatMap((u) => (u.porFicha ?? []).map((f) => f.dataFicha)))
  ).sort();

  const notasPorUsuario = new Map<
    number,
    { qtd: number[]; tempo: number[]; nota: number[] }
  >();
  const ensure = (userId: number) => {
    const atual = notasPorUsuario.get(userId);
    if (atual) return atual;
    const novo = { qtd: [] as number[], tempo: [] as number[], nota: [] as number[] };
    notasPorUsuario.set(userId, novo);
    return novo;
  };

  if (datas.length > 0) {
    for (const dataFicha of datas) {
      const base: DiaPontuavel[] = [];
      for (const usuario of equipe) {
        const ficha = (usuario.porFicha ?? []).find(
          (f) => f.dataFicha === dataFicha
        );
        if (!ficha) continue;
        const tempoMedioMs =
          ficha.tempoMedioMs ??
          (ficha.ocorrencias > 0
            ? Math.round(ficha.tempoTotalMs / ficha.ocorrencias)
            : 0);
        if (ficha.ocorrencias <= 0 && tempoMedioMs <= 0) continue;
        base.push({
          userId: usuario.userId,
          producaoQuantidade: qtyNaUnd(ficha.producaoPorUnd, undNome),
          tempoMedioMs,
          itensEncerrados: ficha.ocorrencias,
        });
      }
      for (const row of pontuarDia(base)) {
        if (row.pontuacao <= 0) continue;
        const acc = ensure(row.userId);
        acc.qtd.push(row.pontuacaoQtd);
        acc.tempo.push(row.pontuacaoTempo);
        acc.nota.push(row.pontuacao);
      }
    }
  } else {
    const base: DiaPontuavel[] = equipe.map((usuario) => ({
      userId: usuario.userId,
      producaoQuantidade: qtyNaUnd(usuario.producaoPorUnd, undNome),
      tempoMedioMs: usuario.tempoMedioMs,
      itensEncerrados: usuario.ocorrencias,
    }));
    for (const row of pontuarDia(base)) {
      if (row.pontuacao <= 0) continue;
      const acc = ensure(row.userId);
      acc.qtd.push(row.pontuacaoQtd);
      acc.tempo.push(row.pontuacaoTempo);
      acc.nota.push(row.pontuacao);
    }
  }

  const visiveisIds = new Set(visiveis.map((u) => u.userId));
  const ranking: PontuacaoFuncionario[] = visiveis
    .filter((u) => visiveisIds.has(u.userId))
    .map((usuario) => {
      const producao = qtyNaUnd(usuario.producaoPorUnd, undNome);
      const notas = notasPorUsuario.get(usuario.userId);
      return {
        userId: usuario.userId,
        userNome: usuario.userNome,
        ocorrencias: usuario.ocorrencias,
        tempoMedioMs: usuario.tempoMedioMs,
        tempoMedioFormatado: usuario.tempoMedioFormatado,
        tempoFormatado: usuario.tempoFormatado,
        producao,
        producaoFormatada: `${producao.toLocaleString("pt-BR", {
          maximumFractionDigits: 3,
        })} ${undNome}`,
        undNome,
        pontuacaoQtd: media(notas?.qtd ?? []),
        pontuacaoTempo: media(notas?.tempo ?? []),
        pontuacao: media(notas?.nota ?? []),
        diasComNota: notas?.nota.length ?? 0,
        notasDias: notas?.nota ?? [],
        posicao: 0,
      };
    })
    .sort((a, b) => {
      if (b.pontuacao !== a.pontuacao) return b.pontuacao - a.pontuacao;
      if (b.diasComNota !== a.diasComNota) return b.diasComNota - a.diasComNota;
      if (b.producao !== a.producao) return b.producao - a.producao;
      if (a.tempoMedioMs !== b.tempoMedioMs) {
        if (a.tempoMedioMs === 0) return 1;
        if (b.tempoMedioMs === 0) return -1;
        return a.tempoMedioMs - b.tempoMedioMs;
      }
      return a.userNome.localeCompare(b.userNome, "pt-BR");
    })
    .map((u, index) => ({ ...u, posicao: index + 1 }));

  return { undNome, ranking };
}
